import { useState, useRef } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ShieldCheck, ShieldAlert, ShieldX, FileUp, Loader2, FileCheck, RotateCcw } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

const VERDICTS = {
  authentic: {
    icon: ShieldCheck, tone: 'text-emerald-600', bg: 'bg-emerald-50 border-emerald-200',
    title: 'Authentic — signature valid',
    desc: 'This receipt was digitally signed by your Smarto and has NOT been modified since download.',
  },
  tampered: {
    icon: ShieldX, tone: 'text-red-600', bg: 'bg-red-50 border-red-200',
    title: 'TAMPERED — content changed after signing',
    desc: 'The document carries a signature, but the bytes no longer match it. Someone edited this file after it was issued.',
  },
  untrusted_signature: {
    icon: ShieldAlert, tone: 'text-amber-600', bg: 'bg-amber-50 border-amber-200',
    title: 'Signed, but not by this system',
    desc: 'A digital signature exists but it does not chain to this deployment\'s signing certificate.',
  },
  unsigned: {
    icon: ShieldAlert, tone: 'text-slate-500', bg: 'bg-slate-50 border-slate-200',
    title: 'No digital signature',
    desc: 'This PDF carries no signature — it may predate signing (older receipts) or come from another source.',
  },
};

export default function ReceiptVerifyPage() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const verify = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Please select a PDF file');
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/vat-tracker/receipts/verify', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Verification failed');
    } finally {
      setBusy(false);
    }
  };

  const v = result ? (VERDICTS[result.verdict] || VERDICTS.unsigned) : null;
  const VIcon = v?.icon;

  return (
    <DashboardLayout>
      <div className="max-w-3xl space-y-6" data-testid="receipt-verify-page">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight flex items-center gap-2">
            <ShieldCheck className="w-6 h-6 text-emerald-600" /> Receipt Verify
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Drop any VAT filing receipt PDF to prove it hasn't been altered since Smarto issued it.
            Receipts are sealed with a PKCS#12 digital signature at generation time.
          </p>
        </div>

        <Card
          className={`border-2 border-dashed transition-colors ${dragOver ? 'border-emerald-400 bg-emerald-50/50' : 'border-slate-300'}`}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); verify(e.dataTransfer.files?.[0]); }}
          data-testid="receipt-verify-dropzone"
        >
          <CardContent className="py-12 flex flex-col items-center gap-3">
            {busy ? (
              <Loader2 className="w-10 h-10 text-emerald-600 animate-spin" data-testid="receipt-verify-spinner" />
            ) : (
              <FileUp className="w-10 h-10 text-slate-400" />
            )}
            <p className="text-sm text-slate-500">Drag a receipt PDF here, or</p>
            <Button
              variant="outline"
              disabled={busy}
              onClick={() => inputRef.current?.click()}
              data-testid="receipt-verify-browse-btn"
            >
              <FileCheck className="w-4 h-4 mr-2" /> Choose PDF
            </Button>
            <input
              ref={inputRef}
              type="file"
              accept="application/pdf"
              className="hidden"
              data-testid="receipt-verify-file-input"
              onChange={(e) => { verify(e.target.files?.[0]); e.target.value = ''; }}
            />
          </CardContent>
        </Card>

        {result && v && (
          <Card className={`border ${v.bg}`} data-testid="receipt-verify-result">
            <CardContent className="py-6 space-y-4">
              <div className="flex items-start gap-3">
                <VIcon className={`w-8 h-8 shrink-0 ${v.tone}`} />
                <div>
                  <div className={`font-bold ${v.tone}`} data-testid="receipt-verify-verdict">{v.title}</div>
                  <p className="text-sm text-slate-600 mt-1">{v.desc}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                <div><div className="text-slate-400 uppercase font-semibold">File</div><div className="truncate" data-testid="receipt-verify-filename">{result.filename}</div></div>
                <div><div className="text-slate-400 uppercase font-semibold">Signature</div><Badge variant={result.has_signature ? 'default' : 'secondary'}>{result.has_signature ? 'present' : 'none'}</Badge></div>
                <div><div className="text-slate-400 uppercase font-semibold">Integrity</div><Badge variant={result.intact ? 'default' : 'destructive'}>{result.intact ? 'intact' : 'broken'}</Badge></div>
                <div><div className="text-slate-400 uppercase font-semibold">Trusted</div><Badge variant={result.trusted ? 'default' : 'secondary'}>{result.trusted ? 'yes' : 'no'}</Badge></div>
              </div>
              {result.signer && (
                <p className="text-xs text-slate-500" data-testid="receipt-verify-signer">Signed by: {result.signer}{result.signed_at ? ` · ${result.signed_at}` : ''}</p>
              )}
              <Button size="sm" variant="ghost" onClick={() => setResult(null)} data-testid="receipt-verify-reset-btn">
                <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Verify another
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
